<script setup lang="ts">
/**
 * PortalHeader · 企业壳层顶部条（68px，--portal-height）
 * ------------------------------------------------------------
 * 对齐 v1.6 原型交互：顶栏只保留高频能力——
 * 左：品牌（点击打开企业官网，官网地址由租户配置下发）· 中：全局搜索（C 位）·
 * 右：云电脑 + 空间 + 邮件 + 日程 + 通知铃铛 + 用户菜单（账号信息 / 个人中心 / 设置中心 / 帮助与文档 / 退出登录）
 * 租户 / 组织 / 外观 / 语言等低频设置全部收进「设置中心」（SettingsModal）。
 */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useScopeStore } from '@/domain/scope/store'
import { authUser, logout, switchTenant, saveNickname } from '@/data/api/auth-store'
import { personMe, ensurePersonMe, clearPersonMe } from '@/data/api/person-store'
import { myDisplayName } from '@/shared/display-name'
import { notifyRepo, mailRepo } from '@/data/api/shell-repository'
import type { NotifItem } from '@/data/api/repository'
import { useToast } from '@/design/ui/UToast'
import GlobalSearch from './search/GlobalSearch.vue'
import SettingsModal from './SettingsModal.vue'
import QuickPop from './QuickPop.vue'
import AiCharacter from './aichar/AiCharacter.vue'
import {
  PORTAL_ACCOUNTS, PRESENCE_LABEL, PRESENCE_ORDER, portalBrandName, portalTenantSite,
  portalAccount, portalNick, portalPresence, portalDept, portalSignature, portalMoments, portalAvatar, accountAvatar,
  setTenant, setPresence, setDept, setSignature, setNick,
  addMoment, updateMoment, removeMoment, syncMoment, switchAccount,
  type Presence, type PortalMoment
} from './portal/portal'
import {
  FEED_SEED, loadDismissed, saveDismissed, applyDismissed,
  type FeedKind, type QuickItem, type DismissMap
} from './portal/quick-feed'

import { isModuleEnabled } from '@/shell/registry'

type PopKind = '' | 'notifications' | 'user'

const router = useRouter()
const scope = useScopeStore()

/* 顶栏图标为纯入口按钮，不与当前所在页做高亮联动；顺序：云电脑 · 空间 · 邮件 · 日程 · 通知。
   四个入口归属可插拔模块，随模块开关显隐（通知铃铛为底座能力，常驻）。 */
const showCloudPc = isModuleEnabled('cloudpc')
const showCloud = isModuleEnabled('cloudspace')
const showMail = isModuleEnabled('mail')
const showCalendar = isModuleEnabled('calendar')
function goCloudPc() { closePop(); void router.push('/cloud-pc') }
function goCloud() { closePop(); void router.push('/cloud-space') }
function goMail() { closePop(); void router.push('/mail') }
function goCalendar() { closePop(); void router.push('/calendar') }

/* 全屏：切换浏览器全屏显示（进入 / 退出），图标随状态互换 */
const isFull = ref(false)
function toggleFull() {
  closePop()
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => { /* 用户拒绝/不支持 */ })
  else void document.documentElement.requestFullscreen().catch(() => { /* 用户拒绝/不支持 */ })
}
function onFsChange() { isFull.value = !!document.fullscreenElement }

const pop = ref<PopKind>('')
const settingsOpen = ref(false)
const toast = useToast()

/* ---------- 身份域交互：在线状态 / 个性签名 / 部门挂职 / 动态 / 多账号切换 ----------
 * 状态与签名/动态按账号保存在本机；切部门/切账号广播 identity:changed，
 * 业务侧按新身份重新取数（切账号同时收敛租户与组织范围）。 */
const ACCOUNT_LIST = Object.values(PORTAL_ACCOUNTS)
const accOpen = ref(false)

const sigEditing = ref(false)
const sigDraft = ref('')
/* 头像悬停浮层内容：有动态展示最新一条（最近背景优先），无动态回落个性签名；
   原生 title 跟随鼠标、提示会被指针遮挡，改为头像正下方弹出的自定义浮层 */
const avTip = computed(() => {
  const m = portalMoments.value[0]
  if (m) return { tag: '最新动态', text: m.text, meta: `${m.time} · ${m.source === 'self' ? '自己发布' : '系统同步'}` }
  return { tag: '个性签名', text: `「${portalSignature.value}」`, meta: '' }
})
const vFocus = { mounted: (el: HTMLInputElement) => el.focus() }
function editSig() { sigDraft.value = portalSignature.value; sigEditing.value = true }
function saveSig() {
  if (!sigEditing.value) return
  const v = sigDraft.value.trim()
  if (v) setSignature(v)
  sigEditing.value = false
}
function cancelSig() { sigDraft.value = portalSignature.value; sigEditing.value = false }

/* 昵称：聊天中对方看到的称呼，已登录时随账号保存到服务端（留空/与本名相同即清除）；
   Esc 取消不落盘；未登录的演示身份仍存本机 */
const nickEditing = ref(false)
const nickDraft = ref('')
const nickSaving = ref(false)
function editNick() {
  nickDraft.value = authUser.value ? (authUser.value.nickname || '').trim() : portalNick.value
  nickEditing.value = true
}
async function saveNick() {
  if (!nickEditing.value || nickSaving.value) return
  const v = nickDraft.value.trim()
  nickEditing.value = false
  if (!authUser.value) {
    const prev = portalNick.value
    setNick(v)
    if (portalNick.value !== prev) toast.show(v && v !== portalAccount.value.name ? '昵称已更新' : '已恢复显示真实姓名')
    return
  }
  const cur = (authUser.value.nickname || '').trim()
  const next = v === authUser.value.name ? '' : v
  if (next === cur) return
  nickSaving.value = true
  try {
    await saveNickname(next)
    toast.show(next ? '昵称已更新，聊天中对方即可看到' : '已清除昵称，聊天中显示真实姓名')
  } catch (e) {
    toast.show(e instanceof Error ? e.message : '昵称保存失败，请稍后重试')
  } finally { nickSaving.value = false }
}
function cancelNick() {
  nickDraft.value = authUser.value ? (authUser.value.nickname || '').trim() : portalNick.value
  nickEditing.value = false
}

/* ---------- 档案卡资料：登录态以 /api/person/me 为准 ----------
 * 工号 / 手机 / 邮箱 / 组织部门来自服务端 person 档案；未登记项不展示（不回落 fixture）。
 * 未登录的演示身份仍沿用壳层 fixture。账号类型标签、部门挂职切换暂无真实口径，仅演示身份展示。 */
watch(authUser, (u) => {
  if (u) void ensurePersonMe(u.personId)
  else clearPersonMe()
}, { immediate: true })

const dispEmpNo = computed(() => authUser.value ? (personMe.value?.empNo ?? '') : portalAccount.value.empNo)
const dispOrg = computed(() => authUser.value ? (personMe.value?.orgName ?? '') : portalAccount.value.org)
const dispDept = computed(() => authUser.value ? (personMe.value?.deptName || '未分配部门') : portalDept.value)
const dispPhone = computed(() => authUser.value ? maskPhone(personMe.value?.phone ?? '') : portalAccount.value.phone)
const dispEmail = computed(() => authUser.value ? (personMe.value?.email ?? '') : portalAccount.value.email)
/** 手机展示口径：账号资料固定掩码（前 3 后 4），不足位时原样 */
function maskPhone(p: string): string {
  const v = p.trim()
  return v.length >= 7 ? v.slice(0, 3) + '****' + v.slice(-4) : v
}

/* 工号：账号中心口径的固定标识，点击复制（clip API 不可用时走 execCommand 兜底） */
function copyEmpNo() {
  const v = dispEmpNo.value
  const ok = () => toast.show(`工号 ${v} 已复制`)
  const legacy = () => {
    const ta = document.createElement('textarea')
    ta.value = v
    ta.style.cssText = 'position:fixed;opacity:0'
    document.body.appendChild(ta)
    ta.select()
    try { document.execCommand('copy') } catch { /* 忽略 */ }
    ta.remove()
  }
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(v).then(ok, () => { legacy(); ok() })
  else { legacy(); ok() }
}

function pickPresence(p: Presence) {
  if (portalPresence.value === p) return
  if (setPresence(p)) toast.show(`状态已切换为「${PRESENCE_LABEL[p]}」`)
}
/* 部门身份：身份行点击部门名展开 pill 一排，点选即切换（切换后系统按新部门身份处理业务） */
const deptOpen = ref(false)
function pickDept(d: string) {
  deptOpen.value = false
  if (d === portalDept.value) return
  if (setDept(d)) toast.show(`已切换至「${d}」身份 · 后续业务将按新部门身份处理`)
}

/* 动态：添加 / 修改自己的动态（momEditId 非空为编辑态），或从系统同步 */
const momDraft = ref<string | null>(null)
const momEditId = ref('')
function startAddMoment() { momEditId.value = ''; momDraft.value = '' }
function editMoment(m: PortalMoment) { momEditId.value = m.id; momDraft.value = m.text }
function saveMoment() {
  const v = (momDraft.value ?? '').trim()
  if (v) (momEditId.value ? updateMoment(momEditId.value, v) : addMoment(v))
  momDraft.value = null
  momEditId.value = ''
}
function cancelMoment() { momDraft.value = null; momEditId.value = '' }
function onSyncMoment() {
  const m = syncMoment()
  toast.show(m ? `已同步系统动态：「${m.text.slice(0, 16)}…」` : '系统动态已是最新，暂无新背景可同步')
}
/* 移除动态：只删这条数据，弹层保持打开（.stop 已阻断冒泡，见模板） */
function onRemoveMoment(id: string) {
  removeMoment(id)
  toast.show('已移除该动态')
}

/** 切换登录账号：仅在本人多账号间切换；身份域换号 + 主属租户/组织一并收敛 */
async function pickAccount(id: string) {
  const acc = switchAccount(id)
  if (!acc) return
  accOpen.value = false
  if (acc.tenant) setTenant(acc.tenant)
  await scope.init()
  toast.show(`已切换至${acc.mode}（${acc.org}）· 数据按新账号身份加载`)
}

/* ---------- 租户：当前租户展示与切换（多租户成员） ----------
 * 会话绑定租户来自 auth-store（登录/me 下发）；切换走 /api/tenant/switch，
 * 成功后整页刷新回首页，彻底清空各 store 与缓存按新租户重新加载（用户 2026-11-01 决定）。 */
const tenantList = computed(() => authUser.value?.tenants ?? [])
const currentTenantId = computed(() => authUser.value?.tenantId ?? null)
const currentTenantName = computed(() => tenantList.value.find(t => t.id === currentTenantId.value)?.name ?? '')
const tenantOpen = ref(false)
const tenantSwitching = ref(false)
async function pickTenant(id: string) {
  if (tenantSwitching.value || id === currentTenantId.value) return
  tenantSwitching.value = true
  try {
    await switchTenant(id)
    window.location.assign('/')
  } catch (e) {
    tenantSwitching.value = false
    tenantOpen.value = false
    toast.show(e instanceof Error ? e.message : '切换租户失败，请稍后重试')
  }
}

/* 退出登录：撤销服务端会话 + 清内存态，回登录页；网络异常也正常退场 */
const loggingOut = ref(false)
async function doLogout() {
  if (loggingOut.value) return
  loggingOut.value = true
  closePop()
  await logout()
  void router.replace('/login')
}

/* 通知中心（platform.notification 服务端持久：列表含已读态，换浏览器不丢；M2+ 接实时推送） */
const notifs = ref<NotifItem[]>([])
const readIds = ref<Set<string>>(new Set())
const notifError=ref(''),notifLoading=ref(false)
async function checkNotifications(){if(notifLoading.value)return;notifLoading.value=true;notifError.value='';notifs.value=[];readIds.value=new Set();try{const items=await notifyRepo.list();notifs.value=items;readIds.value=new Set(items.filter(n=>n.read).map(n=>n.id))}catch(e){notifError.value=e instanceof Error?e.message:'通知未接入'}finally{notifLoading.value=false}}
void checkNotifications()
/* 通知偏好（设置中心可关：off 的分类不显示、不计角标） */
const notifPrefs = ref<Record<string, boolean>>(loadPrefs())
function loadPrefs(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem('ema.notify.v1') || '{}') || {} } catch { return {} }
}
const notifCount = computed(() => notifs.value.filter(n => !readIds.value.has(n.id) && notifPrefs.value[n.tab] !== false).length)
/* 通知不再按待办/审批/公告分页签：任务主页面相关事项在此整体列出（偏好关闭的分类仍不显示） */
const shownNotifs = computed(() => notifs.value.filter(n => notifPrefs.value[n.tab] !== false))
function openNotif(n: NotifItem) {
  readIds.value = new Set(readIds.value).add(n.id)
  void notifyRepo.markRead(n.id).catch(()=>{notifError.value='通知已读操作未接入'})
  closePop()
  if (n.to) router.push(n.to)
}
function readAll() {
  readIds.value = new Set(notifs.value.map(n => n.id))
  void notifyRepo.markAllRead().catch(()=>{notifError.value='通知已读操作未接入'})
}

/* ---------- 图标悬停速览（云电脑 / 云空间 / 邮件 / 日程 / 通知） ----------
 * pc/cloud/calendar 用本地种子，mail 从未读收件箱派生，notif 从未读通知派生；
 * 「忽略」只清本入口提醒（ema.quickfeed.v1），不改邮件已读；通知会同步标记已读。 */
const rawFeed = ref<Record<FeedKind, QuickItem[]>>({
  pc: FEED_SEED.pc ?? [],
  cloud: FEED_SEED.cloud ?? [],
  mail: [],
  calendar: FEED_SEED.calendar ?? [],
  notif: []
})
const dismissed = ref<DismissMap>(loadDismissed())
void mailRepo.list().then(list => {
  rawFeed.value.mail = list
    .filter(m => m.folder === 'inbox' && m.unread)
    .slice(0, 5)
    .map(m => ({ id: 'mail:' + m.id, title: m.subject, sub: `${m.from.name}：${m.preview}`, time: m.time, to: '/mail' }))
})
const notifFeed = computed<QuickItem[]>(() =>
  notifs.value
    .filter(n => !readIds.value.has(n.id) && notifPrefs.value[n.tab] !== false)
    .slice(0, 5)
    .map(n => ({ id: n.id, title: n.title, sub: n.sub, time: n.time, to: n.to }))
)
function itemsOf(kind: FeedKind): QuickItem[] {
  const src = kind === 'notif' ? notifFeed.value : rawFeed.value[kind]
  return applyDismissed(kind, src, dismissed.value)
}
const feedCount = (kind: FeedKind) => itemsOf(kind).length
function ignoreFeed(kind: FeedKind) {
  const ids = itemsOf(kind).map(i => i.id)
  dismissed.value = { ...dismissed.value, [kind]: [...(dismissed.value[kind] || []), ...ids] }
  saveDismissed(dismissed.value)
  if (kind === 'notif') {
    ids.forEach(id => {
      readIds.value = new Set(readIds.value).add(id)
      void notifyRepo.markRead(id).catch(()=>{notifError.value='通知已读操作未接入'})
    })
  }
  hover.value = ''
}
function openFeedItem(item: QuickItem) {
  hover.value = ''
  if (item.to) void router.push(item.to)
}
/* 悬停延迟：首次 200ms 开、180ms 关；已有浮层时跨图标立即切换，移入浮层不收起 */
const hover = ref<FeedKind | ''>('')
let hovOpenT = 0, hovCloseT = 0
function feedEnter(kind: FeedKind) {
  window.clearTimeout(hovCloseT)
  window.clearTimeout(hovOpenT)
  if (hover.value) hover.value = kind
  else hovOpenT = window.setTimeout(() => { hover.value = kind }, 200)
}
function feedLeave() {
  window.clearTimeout(hovOpenT)
  hovCloseT = window.setTimeout(() => { hover.value = '' }, 180)
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
  document.addEventListener('fullscreenchange', onFsChange)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('fullscreenchange', onFsChange)
  window.clearTimeout(hovOpenT)
  window.clearTimeout(hovCloseT)
})

function togglePop(kind: PopKind) {
  hover.value = ''
  if (kind === 'notifications' && pop.value !== 'notifications') notifPrefs.value = loadPrefs()
  if (pop.value === kind) closePop()
  else pop.value = kind
}
function closePop() { pop.value = ''; deptOpen.value = false }
function openSettings() { closePop(); settingsOpen.value = true }

function onDocClick(e: MouseEvent) {
  if (!pop.value) return
  /* 昵称 / 签名 / 动态输入态不因外部点击关弹层（落盘由 blur / 回车 / Esc 负责） */
  if (nickEditing.value || sigEditing.value || momDraft.value !== null) return
  /* composedPath 穿透 shadow DOM（插件注入层可能包一层）：任一节点落在弹层内即不关闭 */
  for (const n of e.composedPath()) {
    if (n instanceof Element && (n.closest('.portal-pop') || n.closest('[data-portal]'))) return
  }
  const t = e.target
  if (!(t instanceof Element)) return
  /* 浏览器插件注入层（如页面审查工具）转发的点击不属于页面内容，不据此关闭浮层 */
  if (t.closest('[class*="trae-browser"]')) return
  closePop()
}
</script>

<template>
  <header class="portal-header">
    <!-- 品牌区：点击在新标签打开企业官网（地址来自租户配置，可在设置中心「外观与语言」修改） -->
    <a class="portal-brand" :href="portalTenantSite" target="_blank" rel="noopener"
       :title="`访问企业官网 · ${portalTenantSite}`" :aria-label="`访问企业官网 ${portalTenantSite}`">
      <span class="portal-mark">企</span>
      <span class="portal-brand-txt">
        <b>{{ portalBrandName }}</b>
        <small>企管助手 · 企业工作空间</small>
      </span>
    </a>

    <div class="portal-search"><GlobalSearch /></div>

    <!-- AI 角色：独立一格的「状态小剧场」（随在线状态换场景；场景注册表见 aichar/scenes） -->
    <div class="portal-pet">
      <AiCharacter />
    </div>

    <nav class="portal-actions" aria-label="云电脑、云空间、邮件、日程、通知、全屏与账号">
      <!-- 云电脑：悬停速览云桌面提醒 -->
      <span v-if="showCloudPc" class="qf" @mouseenter="feedEnter('pc')" @mouseleave="feedLeave">
        <button class="pctl icon" title="云电脑" aria-label="云电脑" @click="goCloudPc">
          <span class="pctl-iw">
            <svg class="pctl-ic" viewBox="0 0 24 24" width="19" height="19" fill="none"
                 stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"
                 aria-hidden="true">
              <rect x="2.5" y="3.5" width="19" height="13" rx="2" />
              <path d="M8 20.5h8M12 16.5v4" />
            </svg>
            <i v-if="feedCount('pc')" class="p-dot" />
          </span>
          <span class="pctl-lb">云电脑</span>
        </button>
        <QuickPop kind="pc" :show="hover === 'pc'" :items="itemsOf('pc')" @open="openFeedItem" @ignore="ignoreFeed('pc')" />
      </span>

      <!-- 云空间：悬停速览共享/同步动态 -->
      <span v-if="showCloud" class="qf" @mouseenter="feedEnter('cloud')" @mouseleave="feedLeave">
        <button class="pctl icon" title="云空间" aria-label="云空间" @click="goCloud">
          <span class="pctl-iw">
            <svg class="pctl-ic" viewBox="0 0 24 24" width="19" height="19" fill="none"
                 stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"
                 aria-hidden="true">
              <path d="M21.5 18.5a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2h4.6l2 3H19.5a2 2 0 0 1 2 2z" />
            </svg>
            <i v-if="feedCount('cloud')" class="p-dot" />
          </span>
          <span class="pctl-lb">云空间</span>
        </button>
        <QuickPop kind="cloud" :show="hover === 'cloud'" :items="itemsOf('cloud')" @open="openFeedItem" @ignore="ignoreFeed('cloud')" />
      </span>

      <!-- 邮件：悬停速览未读邮件 -->
      <span v-if="showMail" class="qf" @mouseenter="feedEnter('mail')" @mouseleave="feedLeave">
        <button class="pctl icon" title="邮件" aria-label="邮件" @click="goMail">
          <span class="pctl-iw">
            <svg class="pctl-ic" viewBox="0 0 24 24" width="19" height="19" fill="none"
                 stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"
                 aria-hidden="true">
              <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
              <path d="m3.5 7.5 8.5 5.5 8.5-5.5" />
            </svg>
            <i v-if="feedCount('mail')" class="p-dot" />
          </span>
          <span class="pctl-lb">邮件</span>
        </button>
        <QuickPop kind="mail" :show="hover === 'mail'" :items="itemsOf('mail')" @open="openFeedItem" @ignore="ignoreFeed('mail')" />
      </span>

      <!-- 日程：悬停速览近期安排（会议管理已并入本页「会议」标签） -->
      <span v-if="showCalendar" class="qf" @mouseenter="feedEnter('calendar')" @mouseleave="feedLeave">
        <button class="pctl icon" title="日程" aria-label="日程" @click="goCalendar">
          <span class="pctl-iw">
            <svg class="pctl-ic" viewBox="0 0 24 24" width="19" height="19" fill="none"
                 stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"
                 aria-hidden="true">
              <rect x="3" y="4.5" width="18" height="17" rx="2.5" />
              <path d="M16 2.5v4M8 2.5v4M3 10.5h18" />
            </svg>
            <i v-if="feedCount('calendar')" class="p-dot" />
          </span>
          <span class="pctl-lb">日程</span>
        </button>
        <QuickPop kind="calendar" :show="hover === 'calendar'" :items="itemsOf('calendar')" @open="openFeedItem" @ignore="ignoreFeed('calendar')" />
      </span>

      <!-- 通知：悬停速览未读，点击展开完整通知中心 -->
      <span class="qf" @mouseenter="feedEnter('notif')" @mouseleave="feedLeave">
        <button class="pctl icon" data-portal="notifications" title="通知中心"
                @click="togglePop('notifications')">
          <span class="pctl-iw">
            <svg class="pctl-bell" viewBox="0 0 24 24" width="19" height="19" fill="none"
                 stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"
                 aria-hidden="true">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.7 21a2 2 0 0 1-3.4 0" />
            </svg>
            <i v-if="notifCount" class="p-badge">{{ notifCount > 99 ? '99+' : notifCount }}</i>
          </span>
          <span class="pctl-lb">通知</span>
        </button>
        <QuickPop v-if="pop !== 'notifications'" kind="notif" :show="hover === 'notif'"
                  :items="itemsOf('notif')" @open="openFeedItem" @ignore="ignoreFeed('notif')" />
      </span>

      <!-- 全屏：切换浏览器全屏显示（图标随状态在进入/退出间互换） -->
      <button class="pctl icon" :title="isFull ? '退出全屏' : '进入全屏'"
              :aria-label="isFull ? '退出全屏' : '进入全屏'" @click="toggleFull">
        <svg class="pctl-ic" viewBox="0 0 24 24" width="19" height="19" fill="none"
             stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"
             aria-hidden="true">
          <g v-if="isFull">
            <path d="M9 4v5H4" /><path d="M15 4v5h5" /><path d="M9 20v-5H4" /><path d="M15 20v-5h5" />
          </g>
          <g v-else>
            <path d="M9 4H4v5" /><path d="M15 4h5v5" /><path d="M9 20H4v-5" /><path d="M15 20h5v-5" />
          </g>
        </svg>
        <span class="pctl-lb">{{ isFull ? '退出' : '全屏' }}</span>
      </button>

      <!-- 用户菜单（头像状态点 / 姓名 / 部门挂职跟随当前账号与身份；头像悬停浮层见 av-tip） -->
      <button class="pctl user" data-portal="user"
              :aria-expanded="pop === 'user'" @click="togglePop('user')">
        <span class="av-wrap">
          <img v-if="portalAvatar" class="portal-avatar" :src="portalAvatar" :alt="portalAccount.name">
          <span v-else class="portal-avatar">{{ authUser ? myDisplayName.charAt(0) : portalAccount.initial }}</span>
          <i class="av-pres" :class="portalPresence" aria-hidden="true"></i>
          <!-- 头像悬停浮层：头像正下方弹出（不被指针遮挡）；有动态看最新动态，无动态看个性签名 -->
          <span class="av-tip" role="tooltip">
            <i class="av-tip-tag">{{ avTip.tag }}</i>
            <span class="av-tip-txt">{{ avTip.text }}</span>
            <span v-if="avTip.meta" class="av-tip-meta">{{ avTip.meta }}</span>
          </span>
        </span>
        <span class="pctl-user">
          <b>{{ myDisplayName }}</b>
          <small>{{ portalDept }}</small>
        </span>
        <span class="pctl-caret" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
               stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6" /></svg>
        </span>
      </button>
    </nav>
  </header>

  <!-- 通知中心 -->
  <Teleport to="body">
    <section v-if="pop === 'notifications'" class="portal-pop notif-pop" role="dialog" aria-label="通知中心">
      <div class="notif-head">
        <b>通知</b>
        <button class="notif-readall" :disabled="!!notifError||!notifs.length" @click="readAll">全部已读</button>
      </div>
      <p v-if="notifError" role="alert">{{notifError}} <button :disabled="notifLoading" @click="checkNotifications">重新检查</button></p>
      <ul v-if="shownNotifs.length" class="notif-list">
        <li v-for="n in shownNotifs" :key="n.id">
          <button class="notif-item" :class="{ unread: !readIds.has(n.id) }" @click="openNotif(n)">
            <i class="ni-dot" />
            <span class="ni-b">
              <b>{{ n.title }}</b>
              <small>{{ n.sub }}</small>
            </span>
            <time>{{ n.time }}</time>
          </button>
        </li>
      </ul>
      <p v-else-if="!notifError" class="notif-empty">暂无通知</p>
    </section>

    <!-- 用户菜单：身份（状态 / 签名 / 部门挂职 / 动态）+ 多账号切换 -->
    <section v-else-if="pop === 'user'" class="portal-pop user-pop" role="dialog" aria-label="账号与设置">
      <!-- 头部档案卡：头像（含状态点）· 昵称（可改，默认真实姓名）· 账号类型 · 工号（点击复制）· 主属组织与部门身份 · 签名 · 账号资料 -->
      <div class="user-head">
        <span class="av-wrap">
          <img v-if="portalAvatar" class="portal-avatar big" :src="portalAvatar" :alt="portalAccount.name + '的头像'">
          <span v-else class="portal-avatar big">{{ authUser ? myDisplayName.charAt(0) : portalAccount.initial }}</span>
          <i class="av-pres" :class="portalPresence" :title="PRESENCE_LABEL[portalPresence]" aria-hidden="true"></i>
        </span>
        <span class="user-head-txt">
          <b class="u-name">
            <template v-if="!nickEditing">
              <span class="u-nick" title="点击修改昵称" @click.stop="editNick">{{ myDisplayName }}</span>
              <i class="u-nick-pen" title="修改昵称" @click.stop="editNick">✎</i>
            </template>
            <input v-else class="u-nick-input" v-model="nickDraft" maxlength="20"
                   placeholder="昵称，留空恢复真实姓名"
                   @keydown.enter="saveNick" @keydown.esc="cancelNick" @blur="saveNick" v-focus>
            <i v-if="!authUser" class="u-mode">{{ portalAccount.mode }}</i>
            <button v-if="dispEmpNo" class="u-empno" type="button" :title="`工号 ${dispEmpNo} · 点击复制`"
                    @click.stop="copyEmpNo">
              <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="12" height="12" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>工号 {{ dispEmpNo }}
            </button>
          </b>
          <small>{{ dispOrg ? dispOrg + '：' : '' }}<span :class="{ 'u-dept-cur': !authUser }"
            :title="authUser ? '主部门（组织口径）' : '点击切换部门身份'"
            @click.stop="!authUser && (deptOpen = !deptOpen)">{{ dispDept }}</span></small>
          <!-- 账号资料：手机 / 企业邮箱（昵称之外的固定身份口径，工号在姓名行）；超长时邮箱优先省略；均未登记时整行不展示 -->
          <small v-if="dispPhone || dispEmail" class="u-idinfo" title="账号资料（账号中心口径）">
            <b v-if="dispPhone">{{ dispPhone }}</b><i v-if="dispPhone && dispEmail">·</i>
            <b v-if="dispEmail" class="u-id-mail" :title="dispEmail">{{ dispEmail }}</b>
          </small>
        </span>
        <!-- 个性签名：档案卡底部通栏（从头像下方开始），过长自动换行展示；点击文字即编辑 -->
        <small class="u-sig-line">
          <template v-if="!sigEditing">
            <span class="u-sig-txt" title="点击编辑个性签名" @click.stop="editSig">「{{ portalSignature }}」</span>
          </template>
          <template v-else>
            <input v-model="sigDraft" maxlength="40" placeholder="写一句个性签名，让大家更了解您"
                   @keydown.enter="saveSig" @keydown.esc="cancelSig" @blur="saveSig" v-focus>
            <button class="u-sig-ok" @mousedown.prevent="saveSig">保存</button>
          </template>
        </small>
      </div>

      <!-- 部门挂职 pill：从身份行展开，点选即切换并收起 -->
      <div v-if="deptOpen" class="u-dept-pills">
        <button v-for="d in portalAccount.depts" :key="d" class="u-dpill" :class="{ on: d === portalDept }"
                :title="d === portalDept ? '当前部门身份' : '切换为该部门身份'" @click.stop="pickDept(d)">{{ d }}</button>
      </div>

      <!-- 在线状态（QQ 式即时状态：在线 / 忙碌 / 离开 / 隐身） -->
      <div class="u-status" role="radiogroup" aria-label="在线状态">
        <button v-for="p in PRESENCE_ORDER" :key="p" class="u-st" :class="{ on: portalPresence === p }"
                role="radio" :aria-checked="portalPresence === p" @click="pickPresence(p)">
          <i class="dot" :class="p" />{{ PRESENCE_LABEL[p] }}
        </button>
      </div>

      <!-- 动态：最近背景情况（出差 / 在飞机上 / 请假等），可自写也可从系统同步 -->
      <div class="u-moments">
        <div class="u-m-head">
          <b>动态</b><small>最近背景 · 与即时状态不同</small>
          <span class="u-m-acts">
            <button title="从系统同步（日程 / 请差旅流程）" @click="onSyncMoment">同步</button>
            <button title="自己写一条动态" @click="startAddMoment">添加</button>
          </span>
        </div>
        <div v-if="momDraft !== null" class="u-m-edit">
          <input v-model="momDraft" maxlength="60" placeholder="如：出差 · 今日乘机途中，回复延迟"
                 @keydown.enter="saveMoment" @keydown.esc="cancelMoment" v-focus>
          <button class="u-m-save" @mousedown.prevent="saveMoment">发布</button>
        </div>
        <ul v-if="portalMoments.length" class="u-m-list">
          <li v-for="m in portalMoments" :key="m.id">
            <i class="m-src" :class="m.source">{{ m.source === 'self' ? '自' : '系' }}</i>
            <span class="m-b">
              <p>{{ m.text }}</p>
              <small>{{ m.time }} · {{ m.source === 'self' ? '自己发布' : '系统同步' }}</small>
            </span>
            <span class="m-acts">
              <button v-if="m.source === 'self'" title="修改这条动态" @click.stop="editMoment(m)">✎</button>
              <button title="移除（仅删除这条数据，弹层保持打开）" @click.stop="onRemoveMoment(m.id)">×</button>
            </span>
          </li>
        </ul>
        <p v-else class="u-m-empty">暂无动态 · 点「添加」写一条，或「同步」从系统拉取</p>
      </div>

      <div class="user-sep" />
      <button class="user-item" @click="openSettings">
        <span class="ui-ic ic-blue" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round"><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" /></svg>
        </span>设置中心<i>›</i>
      </button>

      <!-- 当前租户：多租户成员可在此切换（切换后整页刷新）；单租户成员仅展示当前租户 -->
      <template v-if="tenantList.length">
        <div class="user-sep" />
        <button v-if="tenantList.length > 1" class="user-item" :aria-expanded="tenantOpen" @click="tenantOpen = !tenantOpen">
          <span class="ui-ic ic-blue" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 21v-6h6v6" /></svg>
          </span>{{ currentTenantName || '选择租户' }}<i>{{ tenantOpen ? '收起' : tenantList.length + ' 个租户' }}</i>
        </button>
        <div v-else class="user-item dim">
          <span class="ui-ic ic-blue" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 21v-6h6v6" /></svg>
          </span>{{ currentTenantName }}<i>当前租户</i>
        </div>
        <ul v-if="tenantOpen && tenantList.length > 1" class="u-accounts">
          <li v-for="t in tenantList" :key="t.id">
            <button class="u-acc" :class="{ on: t.id === currentTenantId }"
                    :disabled="t.id === currentTenantId || tenantSwitching" @click="pickTenant(t.id)">
              <span class="u-acc-b">
                <b>{{ t.name }}</b>
                <small>{{ t.code }}<template v-if="t.roles.length"> · {{ t.roles.join('、') }}</template></small>
              </span>
              <i v-if="t.id === currentTenantId" class="u-acc-cur">当前</i>
            </button>
          </li>
        </ul>
      </template>

      <!-- 多账号切换：账号同时在线，直接切换当前登录账号 -->
      <div class="user-sep" />
      <button class="user-item" :aria-expanded="accOpen" @click="accOpen = !accOpen">
        <span class="ui-ic ic-green" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round"><path d="m17 1 4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><path d="m7 23-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></svg>
        </span>切换账号<i>{{ accOpen ? '收起' : ACCOUNT_LIST.length + ' 个账号' }}</i>
      </button>
      <ul v-if="accOpen" class="u-accounts">
        <li v-for="a in ACCOUNT_LIST" :key="a.id">
          <button class="u-acc" :class="{ on: a.id === portalAccount.id }" @click="pickAccount(a.id)">
            <img v-if="accountAvatar(a.id)" class="u-acc-av" :src="accountAvatar(a.id)" :alt="a.name">
            <span v-else class="u-acc-av">{{ a.initial }}</span>
            <span class="u-acc-b">
              <b>{{ a.name }}<small class="u-acc-mode">{{ a.mode }}</small></b>
              <small>{{ a.org }} · {{ a.depts[0] }}</small>
            </span>
            <i v-if="a.id === portalAccount.id" class="u-acc-cur">当前</i>
          </button>
        </li>
      </ul>

      <div class="user-sep" />
      <button class="user-item" :disabled="loggingOut" @click="doLogout">
        <span class="ui-ic ic-red" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></svg>
        </span>{{ loggingOut ? '正在退出…' : '退出登录' }}<i v-if="!loggingOut">›</i>
      </button>
    </section>
  </Teleport>

  <SettingsModal v-model:open="settingsOpen" />
</template>

<style scoped>
.portal-header {
  position: relative; z-index: 310; flex: none;
  height: var(--portal-height);
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 0 24px;
  color: #fff;
  background: linear-gradient(110deg, var(--brand-header), var(--brand-header-end));
  box-shadow: 0 2px 12px #10234816;
}
/* 品牌区吃掉左侧剩余空间（企业名/背景图可能很长），搜索定宽贴右侧铃铛；整体为企业官网外链 */
.portal-brand {
  display: flex; align-items: center; gap: 12px; min-width: 0; flex: 1;
  color: inherit; text-decoration: none; cursor: pointer;
}
/* 悬停不给品牌名加下划线（保持顶栏视觉干净），仅保留指针与键盘焦点提示 */
.portal-brand:focus-visible { outline: 2px solid #ffffffaa; outline-offset: 4px; border-radius: 12px; }
.portal-mark {
  display: grid; place-items: center; width: 38px; height: 38px; flex: none;
  border: 1px solid #ffffff55; border-radius: 11px; background: #ffffff18;
  font-size: 19px; font-weight: 700;
}
.portal-brand-txt { min-width: 0; }
.portal-brand-txt b { display: block; font-size: 17px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 46vw; }
.portal-brand-txt small { display: block; font-size: 12px; opacity: .75; }
.portal-search { flex: none; width: min(320px, 32vw); min-width: 200px; }
.portal-search :deep(.gs-trigger) { width: 100%; background: #ffffff14; border-color: #ffffff38; color: #ffffffcc; }
.portal-search :deep(.gs) { max-width: none; }
.portal-search :deep(.gs-trigger:hover) { background: #ffffff22; border-color: #ffffff55; }
/* 品牌头内搜索为白字透底风格：`/` 快捷键与放大镜键帽盒需同步透底（默认白底会吞掉白图标） */
.portal-search :deep(kbd), .portal-search :deep(.gs-ic) { background: #ffffff18; border-color: #ffffff30; color: #ffffffcc; }

.portal-pet { display: flex; align-items: center; flex: none; }
/* AI 角色：蓝底顶栏上没有气泡时的悬停反馈沿用图标按钮的透白样式 */
.portal-pet :deep(.ai-char:hover) { background: #ffffff16; border-color: #ffffff38; }
.portal-actions { display: flex; align-items: center; gap: 6px; flex: none; }
/* 图标 + 悬停速览的定位容器（浮层为其后代，移入浮层不丢失悬停） */
.qf { position: relative; display: inline-flex; }
/* 头像在线状态点：绿=在线 红=忙碌 黄=离开 灰=隐身 */
.av-wrap { position: relative; display: inline-flex; flex: none; }
.av-pres {
  position: absolute; right: -1px; bottom: -1px;
  width: 11px; height: 11px; border-radius: 50%;
  border: 2.5px solid var(--brand-header-end);
}
.av-pres.online { background: #2fd07c; }
.av-pres.busy { background: #ff5c5c; }
.av-pres.away { background: #f5b028; }
.av-pres.offline { background: #98a2b3; }
/* 头像悬停浮层：头像正下方弹出（原生 title 跟随指针会被遮挡）；
   悬停延迟约 0.4s 后淡入（接近原生 title 节奏），移开立即淡出 */
.av-tip {
  position: absolute; top: calc(100% + 9px); right: -4px;
  width: max-content; max-width: 264px;
  padding: 8px 11px 9px; border-radius: 10px;
  background: #222e3d; color: #fff;
  box-shadow: 0 8px 24px #0f23442e;
  text-align: left; z-index: 24;
  opacity: 0; pointer-events: none;
  transform: translateY(-3px);
  transition: opacity .15s ease, transform .15s ease;
}
.av-wrap:hover .av-tip { opacity: 1; transform: none; transition-delay: .4s; }
/* 用户菜单弹层打开时不再叠加头像浮层（两者位置重叠） */
.pctl.user[aria-expanded="true"] .av-tip { display: none; }
/* 指向头像的小箭头（与浮层同底色的菱形） */
.av-tip::before {
  content: ''; position: absolute; top: -3px; right: 15px;
  width: 9px; height: 9px; border-radius: 2px;
  background: inherit; transform: rotate(45deg);
}
.av-tip-tag {
  display: inline-block; margin-bottom: 3px; font-style: normal;
  font-size: 10px; line-height: 16px; padding: 0 6px; border-radius: 6px;
  background: #ffffff1f; color: #ffffffcc;
}
.av-tip-txt { display: block; font-size: 12px; line-height: 1.55; overflow-wrap: anywhere; }
.av-tip-meta { display: block; margin-top: 3px; font-size: 10.5px; color: #ffffff8c; }
.pctl {
  position: relative;
  display: flex; align-items: center; gap: 9px;
  padding: 7px 11px; min-height: 46px; text-align: left;
  border: 1px solid transparent; border-radius: 10px; color: inherit;
}
.pctl:hover { background: #ffffff16; border-color: #ffffff38; }
/* 图标入口按钮：图标在上、文字在下（title/aria-label 保留，角标锚定图标容器） */
.pctl.icon { flex-direction: column; gap: 3px; padding: 6px 10px 5px; min-width: 52px; }
.pctl-iw { position: relative; display: grid; place-items: center; }
.pctl-lb { font-size: 10px; line-height: 1.25; letter-spacing: .5px; opacity: .85; white-space: nowrap; }
.pctl-bell, .pctl-ic { display: block; }
.pctl small { display: block; font-size: 12px; opacity: .72; line-height: 1.25; }
.pctl b { display: block; font-size: 14px; font-weight: 550; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pctl-user small { display: block; font-size: 12px; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* 用户按钮：放大以匹配 68px 头部（无底色，悬停沿用通用反馈）；负外边距抵消头部内边距，贴近右缘 */
.pctl.user { gap: 4px; padding: 5px 8px 5px 5px; margin-right: -14px; }
.pctl.user .portal-avatar { width: 38px; height: 38px; }
.pctl.user b { font-size: 15px; font-weight: 600; }
.pctl.user small { opacity: .78; }
/* 下拉指示箭头：展开时翻转 */
.pctl-caret { display: grid; place-items: center; opacity: .7; transition: transform .18s, opacity .15s; }
.pctl.user:hover .pctl-caret { opacity: 1; }
.pctl.user[aria-expanded="true"] .pctl-caret { transform: rotate(180deg); opacity: 1; }
.portal-avatar {
  display: grid; place-items: center; width: 34px; height: 34px; flex: none;
  border-radius: 50%; background: #fff; color: var(--brand-header);
  font-size: 14px; font-weight: 650;
}
img.portal-avatar { display: block; object-fit: cover; }
.p-badge {
  position: absolute; top: -6px; right: -13px; font-style: normal;
  font-size: 10px; background: #ff5c5c; color: #fff;
  min-width: 16px; height: 16px; line-height: 16px; text-align: center;
  border-radius: 9px; border: 1.5px solid rgba(0,0,0,.08);
}
/* 隐式提示点：云电脑/云空间/邮件/日程等资讯类入口用琥珀小圆点代替红色数字角标，
   数量细节由悬停速览与各页面承接；仅通知中心保留红色 p-badge（待办/审批需人处理） */
.p-dot {
  position: absolute; top: -4px; right: -5px; font-style: normal;
  width: 7px; height: 7px; border-radius: 50%;
  background: #f5b028; box-shadow: 0 0 0 2px rgba(0, 0, 0, .06);
}

@media (max-width: 1100px) {
  .portal-header { padding: 0 14px; gap: 8px; }
  .portal-brand-txt small { display: none; }
  .pctl-user, .pctl-caret { display: none; }
}
@media (max-width: 760px) {
  .portal-header { height: auto; flex-wrap: wrap; gap: 0; padding: 10px 12px; align-content: space-between; }
  .portal-brand { width: calc(100% - 110px); }
  .portal-mark { width: 30px; height: 30px; font-size: 16px; }
  .portal-brand-txt b { font-size: 15px; }
  .portal-search { order: 3; width: 100%; max-width: none; margin-top: 8px; }
  .portal-actions { gap: 2px; }
  .pctl { padding: 5px 6px; gap: 5px; min-height: 0; }
  .pctl.icon { padding: 7px; min-width: 0; }
  .pctl-lb { display: none; }
  /* 标签隐藏后图标回到垂直居中，AI 小人复位（desktop 为对齐图标行上移了 9px） */
  .portal-pet :deep(.ai-char.dock .ai-stage) { translate: none; }
  .pctl.user { margin-right: 0; }
  .pctl b { font-size: 12px; max-width: 112px; }
  .portal-avatar { width: 30px; height: 30px; font-size: 13px; }
}
</style>

<style>
/* portal-pop · 全局下拉（Teleport 到 body，非 scoped） */
.portal-pop {
  position: fixed; z-index: 330;
  top: calc(var(--portal-height) + 8px); right: 16px;
  width: 340px; max-width: calc(100vw - 24px);
  max-height: calc(100dvh - var(--portal-height) - 80px); overflow: auto;
  border: 1px solid var(--line); border-radius: 14px;
  background: var(--surface); color: var(--ink);
  box-shadow: var(--sh-3); padding: 14px;
}
.notif-head { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid var(--line-soft); padding-bottom: 8px; }
.notif-head b { font-size: 13px; }
.notif-readall { font-size: 12px; color: var(--accent); padding: 4px 8px; }
.notif-list { list-style: none; margin: 0; padding: 4px 0; max-height: 300px; overflow-y: auto; }
.notif-item {
  display: flex; align-items: flex-start; gap: 9px; width: 100%; text-align: left;
  padding: 10px 8px; border-radius: 9px;
}
.notif-item:hover { background: var(--hover); }
.ni-dot { flex: none; width: 7px; height: 7px; border-radius: 50%; background: var(--line); margin-top: 6px; }
.notif-item.unread .ni-dot { background: #ff5c5c; }
.ni-b { flex: 1; min-width: 0; }
.ni-b b { display: block; font-size: 13px; color: var(--ink); font-weight: 550; line-height: 1.4; }
.notif-item.unread .ni-b b { font-weight: 650; }
.ni-b small { display: block; font-size: 12px; color: var(--ink-4); margin-top: 2px; }
.notif-item time { flex: none; font-size: 11px; color: var(--ink-4); margin-top: 2px; }
.notif-empty { padding: 30px 8px 8px; color: var(--ink-3); font-size: 14px; text-align: center; }

.user-pop { padding: 10px; }
/* 头部档案卡：品牌色柔和渐变底 + 圆角卡片，替代原先分割线堆叠的多行小字 */
.user-head {
  display: flex; align-items: center; flex-wrap: wrap; gap: 12px;
  margin: 2px 2px 10px; padding: 12px 12px 13px;
  border: 1px solid var(--line-soft); border-radius: 14px;
  background: linear-gradient(135deg, var(--accent-soft), var(--surface) 62%);
}
.user-head-txt { flex: 1; min-width: 0; }
.portal-avatar.big {
  width: 48px; height: 48px; font-size: 19px;
  background: var(--surface); color: var(--accent);
  box-shadow: 0 0 0 2px var(--surface), 0 2px 10px #0f23442b;
}
.user-head-txt > b { display: block; font-size: 15px; }
:where(.user-head-txt) small { display: block; font-size: 12px; color: var(--ink-3); margin-top: 2px; }
.user-item {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 10px 9px; border-radius: 9px; font-size: var(--fs-sm); color: var(--ink-2); text-align: left;
}
.user-item:hover:not(:disabled) { background: var(--hover); }
.user-item.dim { color: var(--ink-4); cursor: default; }
.user-item.dim:hover { background: none; }
/* 菜单图标：彩色描边 SVG（设置中心=蓝 / 切换账号=绿 / 退出登录=红） */
.ui-ic {
  width: 24px; height: 24px; flex: none; border-radius: 7px;
  display: grid; place-items: center;
}
.ui-ic svg { display: block; }
.ic-blue { background: #e8f1ff; color: #2f6fd6; }
.ic-green { background: #e7f7ef; color: #1f9d61; }
.ic-red { background: #fdecec; color: #d64545; }
.user-item.dim .ui-ic { opacity: .5; }
.user-item i { margin-left: auto; font-style: normal; font-size: 12px; color: var(--ink-4); }
.user-sep { height: 1px; background: var(--line-soft); margin: 6px 4px; }

/* 用户菜单 · 身份扩展（在线状态 / 昵称 / 签名 / 部门挂职 / 动态 / 多账号） */
.user-pop .av-pres { border-color: var(--surface); }
.u-name { display: flex; align-items: center; gap: 4px; min-width: 0; }
.u-nick { cursor: text; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.u-nick:hover { color: var(--accent); }
.u-nick-pen { font-style: normal; flex: none; font-size: 11px; color: var(--ink-4); cursor: pointer; }
.u-nick-pen:hover { color: var(--accent); }
.u-nick-input {
  flex: 1; min-width: 0; max-width: 150px; height: 24px; padding: 0 7px;
  border: 1px solid var(--accent-line); border-radius: 6px;
  font-size: 13px; font-weight: 600; background: var(--surface); color: var(--ink);
}
.u-mode {
  font-style: normal; font-weight: 500; font-size: 10px; margin-left: 7px;
  padding: 1.5px 7px; border-radius: 8px; vertical-align: 1.5px;
  background: var(--accent-soft); color: var(--accent);
}
/* 工号：账号中心口径的固定身份标识 chip，点击复制 */
.u-empno {
  flex: none; display: inline-flex; align-items: center; gap: 4px;
  height: 19px; padding: 0 8px; margin-left: 3px;
  font-size: 10.5px; font-weight: 550; letter-spacing: .2px;
  color: var(--ink-3); background: var(--surface);
  border: 1px solid var(--line); border-radius: 10px;
  cursor: copy;
  transition: color .15s, border-color .15s, background .15s;
}
.u-empno:hover { color: var(--accent); border-color: var(--accent-line); background: var(--accent-soft); }
.u-empno:active { transform: translateY(1px); }
.u-empno svg { flex: none; opacity: .65; }
/* 账号信息（头部 small）：手机 / 企业邮箱；超长时邮箱弹性收缩省略，分隔符不动 */
.u-idinfo {
  display: flex; align-items: center; gap: 7px; min-width: 0;
  max-width: 100%; margin-top: 4px; font-size: 11.5px; color: var(--ink-4);
}
.u-idinfo b { flex: none; font-weight: 500; color: var(--ink-3); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.u-idinfo .u-id-mail { flex: 1; min-width: 0; }
.u-idinfo i { flex: none; font-style: normal; opacity: .55; }
:where(.user-head-txt) small { max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* 在线状态切换（QQ 式） */
.u-status { display: flex; gap: 5px; padding: 10px 8px 2px; }
.u-st {
  flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 5px;
  height: 28px; border-radius: 8px; font-size: 12px; color: var(--ink-3);
  border: 1px solid var(--line-soft);
}
.u-st:hover { background: var(--hover); }
.u-st.on { background: var(--accent-soft); border-color: var(--accent-line); color: var(--accent); font-weight: 600; }
.u-st .dot { width: 8px; height: 8px; border-radius: 50%; }
.u-st .dot.online { background: #2fd07c; }
.u-st .dot.busy { background: #ff5c5c; }
.u-st .dot.away { background: #f5b028; }
.u-st .dot.offline { background: #98a2b3; }
/* 个性签名（档案卡底部通栏，从头像下方开始，点击就地编辑）；过长自动换行展示，「编辑」常驻右上不被挤走 */
.u-sig-line {
  flex: 1 1 100%; display: flex; align-items: flex-start; gap: 6px;
  margin-top: 2px; padding-top: 9px;
  border-top: 1px dashed var(--line-soft);
  font-size: 11.5px; color: var(--ink-3);
}
.u-sig-txt { flex: 1; min-width: 0; line-height: 1.6; white-space: normal; overflow-wrap: anywhere; cursor: text; }
.u-sig-txt:hover { color: var(--ink-2); }
.u-sig-line input { flex: 1; min-width: 0; height: 24px; padding: 0 7px; border: 1px solid var(--accent-line); border-radius: 6px; font-size: 11.5px; background: var(--surface); color: var(--ink); }
.u-sig-ok { flex: none; font-size: 11px; color: var(--accent); padding: 2px 6px; }
/* 部门身份：身份行点击部门名展开 pill 一排，点选即切换 */
.u-dept-cur {
  cursor: pointer; color: var(--accent); font-weight: 600;
  border-bottom: 1px dashed var(--accent-line); padding-bottom: 1px;
}
.u-dept-cur:hover { border-bottom-style: solid; }
.u-dept-pills { display: flex; flex-wrap: wrap; gap: 5px; padding: 9px 8px 1px; }
.u-dpill {
  display: inline-flex; align-items: center; max-width: 100%;
  height: 24px; padding: 0 10px; border-radius: 12px;
  font-size: 11.5px; color: var(--ink-2);
  border: 1px solid var(--line); background: var(--surface);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.u-dpill:hover { border-color: var(--accent-line); background: var(--accent-soft); color: var(--accent); }
.u-dpill.on { border-color: var(--accent-line); background: var(--accent-soft); color: var(--accent); font-weight: 650; }
/* 动态（最近背景情况） */
.u-moments { padding: 6px 6px 2px; }
.u-m-head { display: flex; align-items: baseline; gap: 6px; padding: 0 2px 6px; }
.u-m-head b { font-size: 13px; }
.u-m-head small { font-size: 11px; color: var(--ink-4); }
.u-m-acts { margin-left: auto; display: inline-flex; gap: 2px; }
.u-m-acts button { font-size: 11px; color: var(--accent); padding: 2px 8px; border-radius: 7px; }
.u-m-acts button:hover { background: var(--accent-soft); }
.u-m-edit { display: flex; gap: 6px; padding: 0 2px 8px; }
.u-m-edit input { flex: 1; min-width: 0; height: 28px; padding: 0 8px; border: 1px solid var(--accent-line); border-radius: 8px; font-size: 12px; background: var(--surface); color: var(--ink); }
.u-m-save { flex: none; font-size: 12px; color: #fff; background: var(--accent-solid); border-radius: 8px; padding: 0 12px; }
.u-m-list { list-style: none; margin: 0; padding: 0; max-height: 172px; overflow-y: auto; display: grid; gap: 4px; }
.u-m-list li {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 7px 8px; border-radius: 9px; background: var(--hover);
}
.m-src {
  flex: none; width: 20px; height: 20px; margin-top: 1px;
  border-radius: 6px; display: grid; place-items: center; font-size: 10.5px;
}
.m-src.self { background: var(--accent-soft); color: var(--accent); }
.m-src.system { background: var(--surface-3); color: var(--ink-3); }
.m-b { flex: 1; min-width: 0; }
.m-b p { font-size: 12.5px; line-height: 1.45; color: var(--ink-2); overflow-wrap: anywhere; }
.m-b small { display: block; margin-top: 2px; font-size: 10.5px; color: var(--ink-4); }
.m-acts { flex: none; display: none; gap: 2px; }
.u-m-list li:hover .m-acts { display: inline-flex; }
.m-acts button { width: 20px; height: 20px; border-radius: 6px; font-size: 11px; color: var(--ink-3); display: grid; place-items: center; }
.m-acts button:hover { background: var(--surface-3); color: var(--ink); }
.u-m-empty { padding: 6px 2px 4px; font-size: 11.5px; color: var(--ink-4); text-align: center; }
/* 多账号切换 */
.u-accounts { list-style: none; margin: 2px 0 0; padding: 0 0 2px; display: grid; gap: 2px; }
.u-acc { display: flex; align-items: center; gap: 9px; width: 100%; padding: 7px 9px; border-radius: 9px; text-align: left; }
.u-acc:hover { background: var(--hover); }
.u-acc.on { background: var(--accent-soft); }
.u-acc-av {
  width: 28px; height: 28px; flex: none; border-radius: 50%; object-fit: cover;
  display: grid; place-items: center;
  background: var(--surface-3); color: var(--ink-3); font-size: 12px; font-weight: 650;
}
.u-acc.on .u-acc-av { background: #fff; color: var(--accent); }
.u-acc-b { flex: 1; min-width: 0; }
.u-acc-b b { display: flex; align-items: center; gap: 6px; font-size: 13px; }
.u-acc-mode { font-weight: 500; font-size: 10px; color: var(--ink-4); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.u-acc-b > small { display: block; font-size: 11px; color: var(--ink-4); margin-top: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.u-acc-cur { flex: none; font-style: normal; font-size: 10px; color: var(--accent); border: 1px solid var(--accent-line); border-radius: 7px; padding: 1px 6px; }
</style>
